import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, map, shareReplay } from 'rxjs';
import { TranslocoCurrencyPipe, TranslocoDatePipe } from '@jsverse/transloco-locale';
import { ToolbarComponent } from '../../../shared/components/toolbar/toolbar.component';
import { BillingService } from '../../../financials/services/billing.service';
import { getPaymentProductName, IPayment } from '../../../financials/model/payment.model';

type SubscriberRow = {
    key: string;
    sourceId: string;
    name: string;
    status: string;
    subscribedAt: Date | null;
    lastPaymentAt: Date | null;
    amount: number;
    currency: string;
};

type ProductSubscribersGroup = {
    key: string;
    productId: string;
    productName: string;
    subscribers: SubscriberRow[];
};

type MutableProductSubscribersGroup = {
    key: string;
    productId: string;
    productName: string;
    subscribers: Map<string, SubscriberRow>;
};

@Component({
    selector: 'clp-subscription-subscribers',
    templateUrl: './subscriptionSubscribers.component.html',
    styleUrls: ['./subscriptionSubscribers.component.scss'],
    imports: [
        CommonModule,
        TranslateModule,
        ToolbarComponent,
        TranslocoDatePipe,
        TranslocoCurrencyPipe
    ],
    providers: [
        BillingService
    ]
})
export class SubscriptionSubscribersComponent implements OnInit {
    private billingService = inject(BillingService);

    groups$!: Observable<ProductSubscribersGroup[]>;

    ngOnInit(): void {
        this.groups$ = this.billingService.getPayments().pipe(
            map((payments) => this.buildGroups(payments ?? [])),
            shareReplay(1)
        );
    }

    getTotalSubscribers(groups: ProductSubscribersGroup[]): number {
        return groups.reduce((sum, group) => sum + group.subscribers.length, 0);
    }

    getActiveSubscribers(groups: ProductSubscribersGroup[]): number {
        return groups.reduce((sum, group) =>
            sum + group.subscribers.filter((subscriber) => this.isActiveStatus(subscriber.status)).length, 0);
    }

    getStatusTextKey(status: string | null | undefined): string {
        return `financials.paymentStatus.${this.getCanonicalStatus(status)}`;
    }

    getStatusClass(status: string | null | undefined): string {
        const canonicalStatus = this.getCanonicalStatus(status);

        if (canonicalStatus === 'paid' || canonicalStatus === 'active') {
            return 'status-badge status-success';
        }

        if (canonicalStatus === 'pending') {
            return 'status-badge status-pending';
        }

        if (canonicalStatus === 'cancelled' || canonicalStatus === 'refunded') {
            return 'status-badge status-neutral';
        }

        if (canonicalStatus === 'rejected' || canonicalStatus === 'failed' || canonicalStatus === 'unpaid') {
            return 'status-badge status-danger';
        }

        return 'status-badge status-unknown';
    }

    private buildGroups(payments: IPayment[]): ProductSubscribersGroup[] {
        const groups = new Map<string, MutableProductSubscribersGroup>();

        for (const payment of payments) {
            if (!this.isSubscriptionPayment(payment)) {
                continue;
            }

            const productName = getPaymentProductName(payment) || '-';
            const productId = this.readString(payment, ['productId', 'ProductId']) || productName.toLowerCase();
            const groupKey = `${productId}|${productName.toLowerCase()}`;

            if (!groups.has(groupKey)) {
                groups.set(groupKey, {
                    key: groupKey,
                    productId,
                    productName,
                    subscribers: new Map<string, SubscriberRow>()
                });
            }

            const group = groups.get(groupKey)!;
            const sourceId = this.readString(payment, ['sourceId', 'SourceId']);
            const subscriberName = this.readString(payment, ['userName', 'UserName']);
            const subscriberKey = sourceId || `${subscriberName.toLowerCase()}|${productId}`;
            const subscribedAt = this.readDate(payment, ['createdAt', 'CreatedAt']);
            const status = this.readString(payment, ['subscriptionStatus', 'SubscriptionStatus'])
                || this.readString(payment, ['paymentStatus', 'PaymentStatus'])
                || 'unknown';
            const amount = this.readNumber(payment, ['amount', 'Amount']);
            const currency = this.readString(payment, ['currency', 'Currency']) || 'USD';

            const current = group.subscribers.get(subscriberKey);
            if (!current || this.getTime(subscribedAt) >= this.getTime(current.lastPaymentAt)) {
                group.subscribers.set(subscriberKey, {
                    key: subscriberKey,
                    sourceId,
                    name: subscriberName,
                    status,
                    subscribedAt: current?.subscribedAt ?? subscribedAt,
                    lastPaymentAt: subscribedAt,
                    amount,
                    currency
                });
            }
        }

        return Array.from(groups.values())
            .map((group) => ({
                key: group.key,
                productId: group.productId,
                productName: group.productName,
                subscribers: Array.from(group.subscribers.values()).sort((a, b) => a.name.localeCompare(b.name))
            }))
            .sort((a, b) => b.subscribers.length - a.subscribers.length || a.productName.localeCompare(b.productName));
    }

    private isSubscriptionPayment(payment: IPayment): boolean {
        const sourceType = this.readString(payment, ['sourceType', 'SourceType']).toLowerCase();
        const subscriptionStatus = this.readString(payment, ['subscriptionStatus', 'SubscriptionStatus']);
        return sourceType === 'subscription' || !!subscriptionStatus;
    }

    private readString(payment: IPayment, keys: string[]): string {
        const value = this.readValue(payment, keys);
        return typeof value === 'string' ? value.trim() : '';
    }

    private readNumber(payment: IPayment, keys: string[]): number {
        const value = this.readValue(payment, keys);
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }

        if (typeof value === 'string') {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : 0;
        }

        return 0;
    }

    private readDate(payment: IPayment, keys: string[]): Date | null {
        const value = this.readValue(payment, keys);
        if (!value) {
            return null;
        }

        const date = value instanceof Date ? new Date(value) : new Date(String(value));
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private readValue(payment: IPayment, keys: string[]): unknown {
        const record = payment as unknown as Record<string, unknown>;
        for (const key of keys) {
            if (key in record && record[key] !== undefined && record[key] !== null) {
                return record[key];
            }
        }

        return null;
    }

    private getTime(value: Date | null | undefined): number {
        return value ? value.getTime() : 0;
    }

    private isActiveStatus(status: string | null | undefined): boolean {
        const canonicalStatus = this.getCanonicalStatus(status);
        return canonicalStatus === 'active' || canonicalStatus === 'paid';
    }

    private getCanonicalStatus(status: string | null | undefined): string {
        const normalizedStatus = this.normalizeStatus(status);

        switch (normalizedStatus) {
            case 'active':
            case 'trialing':
                return 'active';

            case 'paid':
            case 'succeeded':
            case 'captured':
            case 'complete':
            case 'completed':
            case 'authorized':
            case 'approved':
                return 'paid';

            case 'cancelled':
            case 'canceled':
            case 'void':
            case 'voided':
            case 'expired':
                return 'cancelled';

            case 'rejected':
            case 'declined':
            case 'paymentrejected':
                return 'rejected';

            case 'failed':
            case 'failure':
            case 'error':
                return 'failed';

            case 'unpaid':
            case 'pastdue':
            case 'overdue':
                return 'unpaid';

            case 'refunded':
            case 'refund':
                return 'refunded';

            case 'pending':
            case 'processing':
            case 'inprogress':
            case 'requiresaction':
            case 'requirescapture':
            case 'requirespaymentmethod':
            case 'open':
                return 'pending';

            default:
                return 'unknown';
        }
    }

    private normalizeStatus(status: string | null | undefined): string {
        if (!status) {
            return '';
        }

        return status.trim().toLowerCase().replace(/[\s_-]+/g, '');
    }
}
